import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

// For an account made by mistake: removes it and everything in it, then signs out
export const DeleteAccountCard = () => {
  const { user, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user || user.is_admin) return null;
  const go = async () => {
    setBusy(true);
    try {
      await api.deleteAccount({ email });
      toast.success("Account deleted");
      await logout();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete the account");
      setBusy(false);
    }
  };
  return (
    <section className="mt-5 rounded-xl border border-rose-400/20 bg-rose-400/[0.04] p-4" data-testid="delete-account-card">
      <p className="text-sm font-medium text-rose-200">Delete this account</p>
      <p className="mt-1 text-xs text-slate-400">Signed in as <b className="text-slate-200" data-testid="delete-account-email">{user.email}</b>. Wrong account? Deleting removes it and everything saved in it. Your other accounts aren't touched.</p>
      <AlertDialog onOpenChange={(o) => { if (!o) setEmail(""); }}>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="outline" className="mt-3 border-rose-400/40 bg-transparent text-rose-200 hover:bg-rose-400/10" data-testid="delete-account-open"><Trash2 size={14} /> Delete account</Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100 w-[calc(100%-2rem)] rounded-2xl" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {user.email}?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone. Type the account's email to confirm.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Type the email" autoCapitalize="none" autoCorrect="off" data-testid="delete-account-input" />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15" data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
            <Button onClick={go} disabled={busy || email.trim().toLowerCase() !== user.email.toLowerCase()} className="bg-rose-500 text-white hover:bg-rose-400" data-testid="delete-account-confirm">Delete forever</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
